/* eslint-disable no-restricted-syntax */
/* global G */
import { PING_URL } from 'lib/util/environment'

const settings = {
  url: PING_URL,
  offlineIntervalMs: 3000,
  intervalMs: 30000,
  timeoutMs: 3000,
}

let worker

/**
 * Instantiates the correct type of worker based on the platform. We need to do this
 * because {@link SharedWorker}s are not available on mobile browsers. Will return
 * the workers {@link MessagePort} object in case shared workers are available, or
 * the worker itself in case they are not.
 *
 * @param {string} path path to the worker's script
 * @param {string} name worker's identifier
 * @returns {MessagePort|SharedWorker|Worker}
 * @private
 */
const _getWorkerInstance = (path, name) => {
  const TargetWorker = typeof SharedWorker !== 'undefined'
    ? new SharedWorker(path, { name })
    : new Worker(path, { name })

  return TargetWorker?.port ?? TargetWorker
}

/**
 * Sets the status of the G.ONLINE session of the application to true if, according to the
 * {@link SharedWorker}, the application has access to the Internet or to false otherwise. It also
 * dispatches that same value through the application's event bus to all listeners of [G.HTTP,
 * G.ONLINE] type.
 *
 * Callback function used to handle messages sent by the {@link SharedWorker} executing the ping
 * script.
 *
 * @param {Gaia.Web.Application} obj  native web application
 */
const handleMessage = obj => async (event) => {
  const sessionState = obj[G.ADAPTER][G.SESSION][G.STATE]
  const eventBus = obj[G.EVENTS]
  const { online } = event.data

  sessionState[G.ONLINE] = online
  eventBus.dispatch(eventBus.type(G.HTTP, G.ONLINE), { online })
}

/**
 * Initializes a {@link SharedWorker} singleton with the ping script, registers
 * {@link handleMessage} as its `onmessage` listener and adds a new listener of type [G.HTTP,
 * G.UPDATE] to `obj`'s event bus to allow sending messages to it from anywhere.
 *
 * @param {Gaia.Web.Application} obj  native web application
 */
const init = (obj) => {
  const eventBus = obj[G.EVENTS]

  if (!worker) {
    worker = _getWorkerInstance('./workers/http/ping.js', 'ping')
    worker.onmessage = handleMessage(obj) // setting onmessage also initializes the WebWorker
    eventBus.add(eventBus.type(G.HTTP, G.UPDATE), () => worker.postMessage({}))
    worker.postMessage({ settings })
  }
}

export default init
